import React, { useEffect, useState } from "react";
import axios from "../../../utils/axios";
import { Spin, message } from "antd";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from "recharts";
import { Users, Smartphone, Utensils } from "lucide-react";

/* =================================================
   QSR DASHBOARD (SAME PATTERN AS EDUCATION)
================================================= */

const QSRDashboard = ({ config }) => {
  const projectId = localStorage.getItem("projectId");

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState({
    liveCount: 0,
    tableOccupancy: 0,
    occupiedTables: 0,
    availableTables: 0,
    mobileUsage: 0,
    trend: []
  });

  /* =================================================
     APIs
  ================================================= */

  const fetchSummary = async () => {
    try {
      const res = await axios.post("/qsr/canteen-summary", { projectId });
      return res.data;
    } catch (err) {
      message.error("Failed to load QSR summary");
      return null;
    }
  };

  const fetchTrend = async () => {
    try {
      const res = await axios.post("/qsr/occupancy-trend", { projectId });
      return res.data;
    } catch {
      return null;
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);

      const summary = await fetchSummary();
      const trend = await fetchTrend();

      if (!summary) return;

      setData({
        liveCount: summary.liveCount,
        tableOccupancy: summary.occupancy,
        occupiedTables: summary.occupied,
        availableTables: summary.available,
        mobileUsage: summary.mobileUsage,
        trend: trend?.graph || []
      });

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // loadData();
  }, []);

  /* =================================================
     COMMON UI (SAME STYLE)
  ================================================= */

  const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border hover:shadow-lg">

      <div className="flex justify-between mb-3">

        <div className={`p-3 rounded-xl bg-gradient-to-br ${color}`}>
          <Icon className="text-white w-5 h-5"/>
        </div>

        <span className="text-xs text-gray-400">
          {subtitle}
        </span>

      </div>

      <p className="text-sm text-gray-500">{title}</p>

      <h3 className="text-3xl font-bold">{value}</h3>

    </div>
  );

  const TrendGraph = () => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border">

      <h3 className="mb-4 font-semibold">
        Occupancy Trend
      </h3>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={data.trend}>
          <CartesianGrid strokeDasharray="3 3"/>
          <XAxis dataKey="time"/>
          <YAxis/>
          <Tooltip/>

          <Area
            type="monotone"
            dataKey="count"
            stroke="#6366f1"
            fillOpacity={0.4}
            fill="#6366f1"
          />

        </AreaChart>
      </ResponsiveContainer>

    </div>
  );

  /* =================================================
     WIDGET REGISTRY (SAME LIKE EDUCATION)
  ================================================= */

  const WIDGETS = {

    "Live Count": () => (
      <MetricCard
        title="People Count"
        value={data.liveCount}
        subtitle="Canteen"
        icon={Users}
        color="from-blue-500 to-indigo-600"
      />
    ),

    "Table Occupancy": () => (
      <MetricCard
        title="Table Occupancy"
        value={`${data.tableOccupancy}%`}
        subtitle="Usage"
        icon={Utensils}
        color="from-orange-500 to-red-500"
      />
    ),

    "Occupied Tables": () => (
      <MetricCard
        title="Occupied Tables"
        value={data.occupiedTables}
        subtitle="In Use"
        icon={Utensils}
        color="from-red-500 to-pink-500"
      />
    ),

    "Available Tables": () => (
      <MetricCard
        title="Available Tables"
        value={data.availableTables}
        subtitle="Free"
        icon={Utensils}
        color="from-green-500 to-emerald-600"
      />
    ),

    "Mobile Usage Alerts": () => (
      <MetricCard
        title="Mobile Usage Alerts"
        value={data.mobileUsage}
        subtitle="Staff"
        icon={Smartphone}
        color="from-purple-500 to-indigo-600"
      />
    ),

    /* 🔥 ADD THESE (MISSING ONES) */

    "Staff Utilization": () => (
      <MetricCard
        title="Staff Utilization"
        value="78%"
        subtitle="Efficiency"
        icon={Users}
        color="from-indigo-500 to-blue-500"
      />
    ),

    "Peak Hour": () => (
      <MetricCard
        title="Peak Hour"
        value="7-8 PM"
        subtitle="High Rush"
        icon={Users}
        color="from-yellow-500 to-orange-500"
      />
    ),

    "System Status": () => (
      <MetricCard
        title="System Status"
        value="Operational"
        subtitle="All Good"
        icon={Users}
        color="from-green-500 to-emerald-600"
      />
    ),

    /* GRAPHS */

    "Table Usage Trend": () => <TrendGraph />,
    "Mobile Usage Trend": () => <TrendGraph />,
    "Occupancy History": () => <TrendGraph />
  };

  /* =================================================
     RENDER (SAME STRUCTURE)
  ================================================= */

  if (loading) return <Spin />;

  return (

    <div className="max-w-7xl mx-auto">

      {config?.rows?.map((row, rowIdx) => {
        return (
          <div
            key={rowIdx}
            className={`grid grid-cols-${row.cards.length} gap-6 mb-6`}
          >

            {row.cards.map((card, idx) => {

              const Widget = WIDGETS[card.name];

              if (!Widget) return null;

              return (
                <div key={idx}>
                  <Widget />
                </div>
              );

            })}

          </div>
        );
      })}

    </div>

  );

};

export default QSRDashboard;